import React, { FunctionComponent, ReactNode, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { Divider, Heading, Icon, Skeleton, Text, useDate } from '@afterpaytouch/core'
import { useFlag } from '../../hooks'
import { OrderSource } from '@afterpaytouch/portal-api/consumer/ordertransactions/types'
import { Money } from '../'
import {
  useGetOrderTransactionQuery,
  useHasOrderRefundedStatus,
  useIsOrderConsumerLending,
  useOrderGetDownpayment,
  useOrderIsPayByInstallment,
} from '../../state/orders/hooks'
import { isValidMoney, isNonZeroMoney } from '../Money/utils'
import { OrderSummaryLine } from './OrderSummaryLines/OrderSummaryLine'
import { OrderSummaryAmount } from './OrderSummaryLines/OrderSummaryAmount'
import { OrderSummaryLinks } from './OrderSummaryLines/OrderSummaryLinks'
import { SupportedLocale } from '@afterpay/types'
import { useIsAFFOrder } from '../OrderDetailPanel/hooks'
import { OrderSnapshot } from '../OrderSnapshot'

const I18N_NAMESPACE = ['common', 'orders']

interface OrderSummaryProps {
  orderId: string
}

export const OrderSummary: FunctionComponent<OrderSummaryProps> = ({ orderId }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { data, isLoading } = useGetOrderTransactionQuery(orderId)
  const isPayByInstallmentOrder = useOrderIsPayByInstallment(orderId)
  const isOrderConsumerLending = useIsOrderConsumerLending(orderId)
  const isAffOrder = useIsAFFOrder(orderId)
  const downPayment = useOrderGetDownpayment(orderId)
  const { orderSummary, loan } = data ?? {}
  const router = useRouter()
  const locale = router?.locale as SupportedLocale
  const { formatUnixDate } = useDate({
    locale,
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })
  const { formatDate } = useDate({
    locale,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  const purchaseDate: string = useMemo(() => {
    // @ts-ignore: OPERATION BLEED STOPPER
    return formatUnixDate(data?.orderDate)
  }, [data?.orderDate, formatUnixDate])

  const isSingleUseCardEnabled = useFlag('single-use-payment-finalized-order-delay', { merchants: [] })
  const hasRefundStatus = useHasOrderRefundedStatus(orderId)

  const isOrderTotalPending = (): boolean => {
    let result = false
    data?.source === OrderSource.SINGLE_USE_CARD &&
      isSingleUseCardEnabled?.merchants?.forEach((merchant) => {
        if (
          String(merchant.id) === String(data?.merchant?.id) &&
          Boolean(merchant?.pendingDuration) &&
          data?.orderDate > Date.now() - 60000 * merchant?.pendingDuration
        ) {
          result = true
        } else {
          result = false
        }
      })
    return result
  }

  const calcTotalCost = (): string | undefined => {
    if (isOrderConsumerLending && loan?.totalCost?.amount) {
      return String(loan.totalCost.amount)
    }

    // For AFF orders, add orderTotal and totalOriginalConsumerFees
    if (isAffOrder) {
      if (!orderSummary?.orderTotal?.amount || !orderSummary?.totalOriginalConsumerFees?.amount) {
        return undefined
      }
      const total = Number(orderSummary.orderTotal.amount) + Number(orderSummary.totalOriginalConsumerFees.amount)
      return String(total.toFixed(2))
    }
    return undefined
  }

  const renderHeading = (): ReactNode => {
    if (isOrderConsumerLending) {
      return t('orders:orderSummary:loanDetails')
    }
    if (isPayByInstallmentOrder && !isAffOrder) {
      return t('orders:orderSummary:orderSummary')
    }
    if (isAffOrder) {
      return <OrderSnapshot orderId={orderId} />
    }
    return null
  }
  const showFeeAdjustment = (isAffOrder && orderSummary?.totalConsumerFeesReturned != null && isNonZeroMoney(orderSummary.totalConsumerFeesReturned)) || false
  const hasValidAfterConsumerRefunds = orderSummary?.totalAfterConsumerRefunds !== undefined && isNonZeroMoney(orderSummary.totalAfterConsumerRefunds)

  const shouldShowAdjustedTotal = (isAffOrder && hasRefundStatus && hasValidAfterConsumerRefunds) || (hasRefundStatus && !isOrderConsumerLending)
  return (
    <>
      <div className='mb-6'>
        <Heading size={'L'} testNameSpace={`order-summary-${isPayByInstallmentOrder ? 'summary' : 'loan'}`}>
          {renderHeading()}
          {isLoading && <Skeleton />}
        </Heading>
      </div>
      <OrderSummaryLine
        isLoading={isLoading}
        item={{
          label: t('orders:orderSummary:purchaseDate'),
          value: purchaseDate,
        }}
        testNameSpace='purchase-date'
        show={isPayByInstallmentOrder && !isAffOrder}
      />
      {isOrderTotalPending() ? (
        <OrderSummaryLine
          isLoading={isLoading}
          item={{ label: t('orders:orderSummary:orderAmount'), value: t('orders:orderSummary:singleUseCard:value') }}
          testNameSpace='sup-total'
          divider={false}
        />
      ) : (
        <OrderSummaryAmount
          isLoading={isLoading}
          item={{
            label: t('orders:orderSummary:orderAmount'),
            // @ts-ignore: OPERATION BLEED STOPPER
            amount: orderSummary?.orderTotal,
          }}
          // @ts-ignore: OPERATION BLEED STOPPER
          subitem={{
            ...(Number.isInteger(data?.exchangeRate) && {
              label: (
                <Trans
                  values={{ exchange: String(data?.exchangeRate) }}
                  components={{
                    // @ts-ignore: OPERATION BLEED STOPPER
                    amount: <Money value={data?.merchantAmount} size='S' />,
                  }}
                  i18nKey='orders:orderSummary:exchange'
                />
              ),
              amount: data?.merchantAmount,
            }),
          }}
          testNameSpace='total'
          divider={false}
        />
      )}
      {orderSummary?.donation != null && (
        <OrderSummaryLine
          item={{
            label: t('orders:orderSummary:donationAmount'),
            value: (
              <>
                <Money value={orderSummary.donation.amount} size='M' />
              </>
            ),
          }}
          isLoading={isLoading}
          testNameSpace='donation'
          divider={false}
        />
      )}
      <OrderSummaryAmount
        item={{
          label: t('orders:orderSummary:promolabel'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: orderSummary?.promoDiscount?.totalPromoDiscountAmount,
        }}
        prefix='-'
        badge
        // @ts-ignore: OPERATION BLEED STOPPER
        show={isValidMoney(orderSummary?.promoDiscount?.totalPromoDiscountAmount)}
      />
      <OrderSummaryAmount
        item={{
          label: t('orders:orderSummary:returns'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: orderSummary?.totalRefunded,
        }}
        prefix='-'
        testNameSpace='returns'
        // @ts-ignore: OPERATION BLEED STOPPER
        show={isValidMoney(orderSummary?.totalRefunded)}
        divider={false}
      />
      <Divider kind='Hairline' />

      {/* Consumer lending fields */}
      <OrderSummaryAmount
        isLoading={isLoading}
        item={{
          label: t('orders:orderSummary:downPayment'),
          amount: downPayment,
        }}
        testNameSpace='downpayment'
        show={isOrderConsumerLending && isValidMoney(downPayment)}
      />
      <OrderSummaryAmount
        isLoading={isLoading}
        item={{
          label: t('orders:orderSummary:amountFinanced'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: loan?.amountFinanced,
        }}
        testNameSpace='amount-financed'
        // @ts-ignore: OPERATION BLEED STOPPER
        show={isValidMoney(loan?.amountFinanced)}
      />
      <OrderSummaryAmount
        isLoading={isLoading}
        item={{
          label: t('orders:orderSummary:financeFee'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: orderSummary?.totalOriginalConsumerFees,
        }}
        testNameSpace='finance-fee'
        // @ts-ignore: OPERATION BLEED STOPPER
        show={isAffOrder && isValidMoney(orderSummary?.totalOriginalConsumerFees)}
        divider={!showFeeAdjustment}
      />
      <OrderSummaryAmount
        item={{
          label: t('orders:orderSummary:financeFeeAdjustment'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: orderSummary?.totalConsumerFeesReturned,
        }}
        testNameSpace='returns'
        // @ts-ignore: OPERATION BLEED STOPPER
        show={isAffOrder && showFeeAdjustment}
      />
      <OrderSummaryAmount
        isLoading={isLoading}
        item={{
          label: t('orders:orderSummary:totalInterest', { apr: loan?.effectiveApr }),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: loan?.totalInterest,
        }}
        sublabel={
          loan?.hasInterestRateChanged ?? false ? (
            <Text renderAs='span' size='M' color='Gray50'>
              {t('orders:orderSummary:updatedInterest', { apr: loan?.currentInterestRate, date: formatDate(loan?.currentInterestRateStartDate) })}
            </Text>
          ) : null
        }
        testNameSpace='total-interest'
        show={isOrderConsumerLending}
      />
      <OrderSummaryAmount
        isLoading={isLoading}
        item={{
          label: t('orders:orderSummary:totalCost'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: { amount: calcTotalCost(), currency: orderSummary?.orderTotal?.currency },
        }}
        testNameSpace='total-cost'
        // @ts-ignore: OPERATION BLEED STOPPER
        show={(isAffOrder && isValidMoney(orderSummary?.totalOriginalConsumerFees)) || isValidMoney(loan?.totalCost)}
      />

      {/* Only show adjusted total field if they have had a refund on the order */}
      <OrderSummaryAmount
        item={{
          label: t('orders:orderSummary:adjustedTotal'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: isAffOrder ? orderSummary?.totalAfterConsumerRefunds : orderSummary?.totalAfterRefund,
        }}
        testNameSpace='adjusted-total'
        show={shouldShowAdjustedTotal}
      />
      <OrderSummaryAmount
        isLoading={isLoading}
        item={{
          label: t('orders:orderSummary:totalPaid'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: orderSummary?.totalPaid,
        }}
        // NEW
        sublabel={
          Number(orderSummary?.totalPaidFee?.amount) !== 0 && (
            <div className='flex justify-end'>
              <Text renderAs='span' size='M'>
                <Trans
                  components={{
                    // @ts-ignore: OPERATION BLEED STOPPER
                    amount: <Money value={orderSummary?.totalPaidFee} size='S' />,
                  }}
                  i18nKey='orders:orderSummary:totalPaidFee'
                />
              </Text>
            </div>
          )
        }
      />
      <OrderSummaryAmount
        item={{
          label: t('orders:orderSummary:returnToCard'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: orderSummary?.totalRefundedToCard,
        }}
        sublabel={
          isOrderConsumerLending && (
            <Text renderAs='span' size='M' color='Gray50'>
              <OrderSummaryCaret label={t('orders:orderSummary:interestNonRefundable')} testNameSpace='interest-non-refundable'>
                {t('orders:orderSummary:interestNonRefundableReason')}
              </OrderSummaryCaret>
            </Text>
          )
        }
        subitem={{
          label: (
            <Trans
              components={{
                // @ts-ignore: OPERATION BLEED STOPPER
                amount: <Money value={orderSummary?.totalRefundedToCardFee} size='S' />,
              }}
              i18nKey='orders:orderSummary:totalRefundedToCardFee'
            />
          ),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: orderSummary?.totalRefundedToCardFee,
        }}
        testNameSpace='return-to-card'
        prefix='+'
        // @ts-ignore: OPERATION BLEED STOPPER
        show={isValidMoney(orderSummary?.totalRefundedToCard)}
      />
      <OrderSummaryAmount
        item={{
          label: t('orders:orderSummary:paymentPending'),
          // @ts-ignore: OPERATION BLEED STOPPER
          amount: orderSummary?.totalAmountPending,
        }}
        // @ts-ignore: OPERATION BLEED STOPPER
        show={isPayByInstallmentOrder && isValidMoney(orderSummary?.totalAmountPending)}
      />
      {isOrderTotalPending() ? (
        <OrderSummaryLine
          isLoading={isLoading}
          item={{ label: t('orders:orderSummary:singleUseCard:label'), value: <Text bold>{t('orders:orderSummary:singleUseCard:value')}</Text> }}
        />
      ) : (
        <OrderSummaryAmount
          isLoading={isLoading}
          item={{
            label: t('orders:orderSummary:totalAmountOwing'),
            // @ts-ignore: OPERATION BLEED STOPPER
            amount: orderSummary?.totalOwed,
          }}
          subitem={{
            label: (
              <Trans
                components={{
                  // @ts-ignore: OPERATION BLEED STOPPER
                  amount: <Money value={orderSummary?.totalOwedFee} size='S' />,
                }}
                i18nKey='orders:orderSummary:totalAmountOwingFee'
              />
            ),
            // @ts-ignore: OPERATION BLEED STOPPER
            amount: orderSummary?.totalOwedFee,
          }}
          show={isPayByInstallmentOrder && !isAffOrder}
        />
      )}
      {isPayByInstallmentOrder && !isAffOrder && <OrderSummaryLinks orderId={orderId} />}
    </>
  )
}

interface OrderSummaryCaretProps {
  label: ReactNode
  testNameSpace: string
  children: ReactNode
}

const OrderSummaryCaret: FunctionComponent<OrderSummaryCaretProps> = ({ label, testNameSpace, children }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  return (
    <>
      <div className='hover:cursor-pointer' onClick={() => setIsOpen(!isOpen)} data-testid={`order-summary-${testNameSpace}-caret`}>
        {label}
        <span className='ml-1'>
          <Icon name={isOpen ? 'CaretUp' : 'CaretDown'} size='Mini' title={t(`orders:orderSummary:caretLabels:${isOpen ? 'hide' : 'display'}`)} role='img' />
        </span>
      </div>
      {isOpen && <div className='mt-2'>{children}</div>}
    </>
  )
}
